import type { RefCallback } from 'react';
import type { RecordKey } from '@ricoh/ria.client.core/types/core/data.type';
import type { EmptyObject } from 'type-fest';

import { useCallback, useMemo, useRef } from 'react';

import type { RefList, RefRecord, RefSettable } from './ref.type';

/**
 * Use Ref Settable.
 *
 * @template TObject Object ref type.
 * @param {TObject} initialValue Initial value.
 * @param {RefCallback<TObject>} trailingSetter Trailing handler after setting ref.
 *
 * @returns {RefSettable<TObject>} Ref Settable.
 */
export function useRefSettable<TObject>(initialValue: TObject | null, trailingSetter?: RefCallback<TObject>): RefSettable<TObject> {
  const ref = useRef<TObject | null>(initialValue);

  const setRef = useCallback<RefSettable<TObject>['ref']>(
    (obj) => {
      ref.current = obj;
      if (trailingSetter) {
        trailingSetter(obj);
      }
    },
    [trailingSetter],
  );

  return useMemo<RefSettable<TObject>>(
    () =>
      Object.assign<RefSettable<TObject>>(ref, {
        ref: setRef,
      }),
    [setRef],
  );
}

/**
 * Use RefList.
 *
 * @template TObject Object ref type.
 * @param {TObject[]} initialValue Initial value.
 * @param {RefCallback<TObject>} trailingSetter Trailing handler after setting ref.
 *
 * @returns {RefList<TObject>} ListRef.
 */
export function useRefList<TObject>(initialValue: TObject[], trailingSetter?: RefCallback<TObject>): RefList<TObject> {
  const ref = useRef<(TObject | null)[]>(initialValue);

  const setRef = useCallback<RefList<TObject>['ref']>(
    (index) => (obj) => {
      ref.current[index] = obj;
      if (trailingSetter) {
        trailingSetter(obj);
      }
    },
    [trailingSetter],
  );

  return useMemo<RefList<TObject>>(
    () =>
      Object.assign<RefList<TObject>>(ref, {
        ref: setRef,
      }),
    [setRef],
  );
}

/**
 * Use RecordRef.
 *
 * @template TKey Key type.
 * @template TObject Object ref type.
 * @param {Record<string, TObject>} initialValue Initial value.
 * @param {RefCallback<TObject>} trailingSetter Trailing handler after setting ref.
 *
 * @returns {RefRecord<TKey, TObject>} RecordRef.
 */
export function useRefRecord<TKey extends RecordKey, TObject>(
  initialValue: Record<TKey, TObject> | EmptyObject,
  trailingSetter?: RefCallback<TObject>,
): RefRecord<TKey, TObject> {
  const ref = useRef<Record<TKey, TObject | null>>(initialValue as Record<TKey, TObject | null>);

  const setRef = useCallback<RefRecord<TKey, TObject>['ref']>(
    (key) => (obj) => {
      ref.current[key] = obj;
      if (trailingSetter) {
        trailingSetter(obj);
      }
    },
    [trailingSetter],
  );

  return useMemo<RefRecord<TKey, TObject>>(
    () =>
      Object.assign<RefRecord<TKey, TObject>>(ref, {
        ref: setRef,
      }),
    [setRef],
  );
}
